import React, { useEffect, useState, useCallback } from "react";
import styles from "./style.module.scss";
import PopupSequence from "./PopupSequence/PopupSequence";
import AnalysisBoard from "./AnalysisBoard/AnalysisBoard";
import { apiRequest } from "util/services";
import { useHistory } from "react-router-dom";
import { Button, notification, Spin } from "antd";
import { LinkOutlined, MailOutlined, RiseOutlined } from "@ant-design/icons";
import { convertDateIntoYYYYMMDD } from "util/common";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { SET_USER } from "redux/user/actions.js";
import { hasMarketIntelligencePlan } from "util/marketIntelligenceAccess";
import {
  trackAddonLockCtaClicked,
  trackAddonLockViewed,
} from "util/productAnalytics";
import MarketIntelligenceLockBrands from "./MarketIntelligenceLockBrands";

const MARKET_INTELLIGENCE_PRODUCT_URL =
  "https://pivony.com/products/market-intelligence";

const IndustryDashboard = ({ authenticated, user, match }) => {
  //init states
  const [comparisonViewEmpty, setComparisonViewEmpty] = useState(true);
  const [data, setData] = useState([]);
  const [vocData, setVocData] = useState(null);
  const [selectedConfiguration, setSelectedConfiguration] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [availablePlatforms, setAvailablePlatforms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [comparisonName, setComparisonName] = useState("Comparison");
  const [defaultSelectedCategories, setDefaultSelectedCategories] = useState([]);
  const [subscriptionFetchFinished, setSubscriptionFetchFinished] = useState(false);

  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const subscriptionStatus = useSelector((state) => state.userReducer);
  const theme = useSelector((state) => state.DashboardReducer.theme);
  const subscriptionHydrated =
    subscriptionStatus?.PackageId != null ||
    subscriptionStatus?.PackageName != null ||
    subscriptionFetchFinished;
  const canAccessMarketIntelligence = hasMarketIntelligencePlan(subscriptionStatus);

  useEffect(() => {
    if (
      authenticated === "logged" &&
      subscriptionHydrated &&
      !canAccessMarketIntelligence
    ) {
      trackAddonLockViewed("market_intelligence");
    }
  }, [authenticated, subscriptionHydrated, canAccessMarketIntelligence]);

  useEffect(() => {
    setSubscriptionFetchFinished(false);
  }, [user?.uid]);

  useEffect(() => {
    if (authenticated !== "logged") return;
    const hasRow =
      subscriptionStatus?.PackageId != null ||
      subscriptionStatus?.PackageName != null;
    if (hasRow || !user?.uid) return;

    let cancelled = false;
    apiRequest(
      "subscriptions/all",
      "POST",
      { user_id: user.uid },
      true
    ).then((result) => {
      if (cancelled) return;
      setSubscriptionFetchFinished(true);
      if (!result?.error && result?.[0]) {
        dispatch({ type: SET_USER, payload: result[0] });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [
    authenticated,
    user?.uid,
    subscriptionStatus?.PackageId,
    subscriptionStatus?.PackageName,
    dispatch,
  ]);
  const params = new URLSearchParams(window.location.search);
  const selectedChartType = params.get("selected_chart_type");

  const updateQParams = (attr, val) => {
    params.delete(attr);
    params.append(attr, val);
    history.push({ search: params.toString() });
  };

  // Memoized function to build payload from URL params
  const buildPayloadFromParams = useCallback(() => {
    const payload = {
      filters: {},
    };
    
    if (params.get("selected_categories")) {
      payload.filters["categories"] = JSON.parse(params.get("selected_categories"));
    }
    
    if (params.get("selected_sentiment")) {
      payload.filters["sentiment"] = JSON.parse(params.get("selected_sentiment"));
    } else {
      payload.filters["sentiment"] = [];
    }
    
    if (params.get("selected_platforms")) {
      payload.filters["stream"] = JSON.parse(params.get("selected_platforms"));
    } else {
      payload.filters["stream"] = [];
    }
    
    if (params.get("since") && params.get("until")) {
      payload.filters["date"] = [params.get("since"), params.get("until")];
    } else {
      if (params.get("daysRange") === "allTime") {
        payload.filters["date"] = [];
      } else {
        const untilDate = new Date();
        const sinceDate = new Date(untilDate.getTime() - 6 * 24 * 60 * 60 * 1000);
        payload.filters["date"] = [
          convertDateIntoYYYYMMDD(sinceDate),
          convertDateIntoYYYYMMDD(untilDate),
        ];
        updateQParams("since", convertDateIntoYYYYMMDD(sinceDate));
        updateQParams("until", convertDateIntoYYYYMMDD(untilDate));
        updateQParams("daysRange", "last7d");
      }
    }
    
    return payload;
  }, [window.location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  //get streams and categories
  const fetchDropdownData = async () => {
    const categoryApiResponse = await apiRequest(
      "industry_analysis/categoryStatus",
      "GET",
      null,
      true
    );
    if (categoryApiResponse?.data) {
      setDefaultSelectedCategories(
        categoryApiResponse.data.filter((item) => item.status === 1)
      );
      if (params.get("selected_categories")) {
        const selectedCatIds = JSON.parse(params.get("selected_categories"));
        const newCategories = categoryApiResponse.data.map((item) => {
          return {
            ...item,
            status: selectedCatIds.includes(item.categoryId) ? 1 : 0,
          };
        });
        setCategories(newCategories);
      } else {
        const selectedCat = categoryApiResponse.data.filter(
          (item) => item.status === 1
        );
        updateQParams(
          "selected_categories",
          JSON.stringify(selectedCat?.map((item) => item.categoryId))
        );
        setCategories(categoryApiResponse.data);
      }
    } else {
      notification.error({
        message: `${categoryApiResponse?.error}`,
      });
    }

    const streamApiResponse = await apiRequest(
      "industry_analysis/streams",
      "GET",
      null,
      true
    );
    if (streamApiResponse?.data) {
      setAvailablePlatforms(streamApiResponse.data);
    } else {
      notification.error({
        message: `${streamApiResponse?.error}`,
      });
    }
  };

  const fetchData = async (payload) => {
    setIsLoading(true);
    try {
      if (params.get("selected_chart_type") === "C4") {
        const apiResponse = await apiRequest(
          "industry_analysis/v2/loadCharts/voc",
          "POST",
          payload,
          true
        );

        if (apiResponse?.success) {
          setIsLoading(false);
          setComparisonViewEmpty(false);
          setVocData(apiResponse?.success?.data);
          setData(null); // Clear regular chart data
          setComparisonName(apiResponse?.success?.viewName);
        } else {
          setComparisonViewEmpty(true);
          setVocData(null);
          setIsLoading(false);
        }
      } else {
        const apiResponse = await apiRequest(
          "industry_analysis/v2/loadCharts",
          "POST",
          payload,
          true
        );
        if (apiResponse?.success) {
          setIsLoading(false);
          setComparisonViewEmpty(false);
          setData(apiResponse?.success?.data);
          setVocData(null); // Clear VOC data
          setComparisonName(apiResponse?.success?.data?.viewName);
        } else if (apiResponse?.error === "No configuration found") {
          setIsLoading(false);
          setComparisonViewEmpty(true);
          setData(null);
          setVocData(null);
        } else {
          notification.error({
            message: `${apiResponse?.error}`,
          });
          setIsLoading(false);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      setIsLoading(false);
      notification.error({
        message: "An error occurred while fetching data",
      });
    }
  };

  useEffect(() => {
    //if user is not logged in or was logged out redirect out
    if (authenticated !== "logged") {
      history.push("/");
      return;
    }
    if (!subscriptionHydrated || !canAccessMarketIntelligence) {
      return;
    }
    fetchDropdownData();
  }, [subscriptionHydrated, canAccessMarketIntelligence]); // eslint-disable-line react-hooks/exhaustive-deps

  //check user comparison view when page loads and when chart type changes
  useEffect(() => {
    if (!canAccessMarketIntelligence) {
      return;
    }
    // Only fetch data if categories have been loaded (to avoid race conditions)
    if (categories.length > 0 || params.get("selected_categories")) {
      const payload = buildPayloadFromParams();
      fetchData(payload);
    }
  }, [selectedChartType, categories.length, buildPayloadFromParams, canAccessMarketIntelligence]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!canAccessMarketIntelligence) {
      return;
    }
    //If user doesn't have a saved configuration, Load the charts from the selected configuration
    if (selectedConfiguration) {
      fetchData(selectedConfiguration);
    }
  }, [selectedConfiguration, canAccessMarketIntelligence]); // eslint-disable-line react-hooks/exhaustive-deps

  const clearComparisonView = () => {
    setComparisonViewEmpty(true);
    setData(null);
    setVocData(null);
  };

  if (authenticated === "logged" && !subscriptionHydrated) {
    return (
      <div className={styles.mainWindow}>
        <div className={styles.Spinner}>
          <Spin className={styles.spin} size="large" spinning />
        </div>
      </div>
    );
  }

  if (authenticated === "logged" && subscriptionHydrated && !canAccessMarketIntelligence) {
    return (
      <div
        className={`${styles.mainWindow} ${styles.marketLockedPage} ${
          theme === "dark" ? styles.marketLockedPageDark : styles.marketLockedPageLight
        }`}
      >
        <div className={styles.marketBackground}>
          <div className={styles.marketGrid} />
          <div className={styles.marketOrbOne} />
          <div className={styles.marketOrbTwo} />
          <div className={styles.marketCardGhostOne}>
            <span>Market share</span>
            <strong>+18%</strong>
          </div>
          <div className={styles.marketCardGhostTwo}>
            <span>Competitor signals</span>
            <strong>Live</strong>
          </div>
          <div className={styles.marketTrendLine} />
        </div>
        <div className={styles.marketLockedModal}>
          <div className={styles.marketLockedIcon}>
            <RiseOutlined />
          </div>
          <p className={styles.marketLockedEyebrow}>
            {t("market_intelligence_lock.eyebrow")}
          </p>
          <h1>{t("market_intelligence_lock.heading")}</h1>
          <p className={styles.marketLockedText}>
            {t("market_intelligence_lock.body")}
          </p>
          <div className={styles.marketLockedActions}>
            <Button
              type="primary"
              size="large"
              icon={<MailOutlined />}
              href="mailto:hello@pivony.com?subject=Market%20Intelligence%20Plan"
              className={styles.marketLockedCtaPrimary}
              onClick={() =>
                trackAddonLockCtaClicked("market_intelligence", "email")
              }
            >
              {t("product_lock.talk_to_sales")}
            </Button>
            <Button
              size="large"
              icon={<LinkOutlined />}
              href={MARKET_INTELLIGENCE_PRODUCT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.marketLockedCtaSecondary}
              onClick={() =>
                trackAddonLockCtaClicked("market_intelligence", "read_more")
              }
            >
              {t("product_lock.upgrade_demo")}
            </Button>
          </div>
          <MarketIntelligenceLockBrands />
        </div>
      </div>
    );
  }

  return (
    <div className={styles.mainWindow}>
      <div className={styles.Heading}>
        <div className={styles.title}>{t("dashboard_sidebar.9")}</div>
      </div>
      <div className={styles.content}>
        {comparisonViewEmpty ? (
          //case 1 comparison view is empty
          <div className={styles.landingDisplay}>
            {isLoading && (
              <div className={styles.Spinner}>
                <Spin
                  className={styles.spin}
                  size="large"
                  spinning={isLoading}
                />
              </div>
            )}
            <div className={styles.start}>
              <h4 className={styles.displayMsg}>
                {t("competitor_analysis.10")}
              </h4>
              <PopupSequence
                setSelectedConfiguration={setSelectedConfiguration}
                setIsLoading={setIsLoading}
              />
            </div>
          </div>
        ) : (
          //case 2 comparison view needs to contain data
          <div className={styles.Analysis}>
            {isLoading && (
              <div className={styles.Spinner}>
                <Spin
                  className={styles.spin}
                  size="large"
                  spinning={isLoading}
                />
              </div>
            )}
            <AnalysisBoard
              data={data}
              vocData={vocData}
              dataLoaded={dataLoaded}
              setDataLoaded={setDataLoaded}
              fetchData={(payload) => fetchData(payload)}
              availablePlatforms={availablePlatforms}
              defaultSelectedCategories={defaultSelectedCategories}
              categories={categories}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              clearComparisonView={clearComparisonView}
              comparisonName={comparisonName ? comparisonName : "Comparison"}
              setComparisonName={setComparisonName}
              match={match}
              user={user}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default IndustryDashboard;